import { LitElement, PropertyValues, css, html, nothing } from 'lit';

export interface SessionWarningModalViewModel {
  open: boolean;
  title: string;
  message: string;
  secondsRemaining?: number;
}

export class SessionWarningModal extends LitElement {
  public static readonly properties = {
    onLogOut: { attribute: false },
    onStaySignedIn: { attribute: false },
    viewModel: { attribute: false }
  };

  public static readonly styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: 1000;
      display: block;
    }

    .backdrop {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.5rem;
      background: rgba(0, 48, 135, 0.5);
    }

    .modal {
      width: min(100%, 32rem);
      padding: 1.5rem;
      background: #ffffff;
      border-top: 0.5rem solid #ffb81c;
      border-radius: 0.25rem;
      box-shadow: 0 1.5rem 2.5rem rgba(0, 48, 135, 0.2);
      color: #212b32;
      outline: none;
    }

    .eyebrow {
      margin: 0 0 0.5rem;
      color: #005eb8;
      font-size: 0.875rem;
      font-weight: 700;
      letter-spacing: 0.03em;
      text-transform: uppercase;
    }

    h2 {
      margin: 0 0 0.75rem;
      color: #212b32;
      font-size: 1.75rem;
      line-height: 1.2;
    }

    p {
      margin: 0;
      font-size: 1rem;
      line-height: 1.5;
    }

    .countdown {
      display: inline-block;
      margin-top: 0.75rem;
      font-weight: 700;
      color: #594d00;
    }

    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      margin-top: 1rem;
    }
  `;

  public onLogOut?: () => void;

  public onStaySignedIn?: () => void;

  public viewModel: SessionWarningModalViewModel = {
    open: false,
    title: '',
    message: ''
  };

  protected updated(changedProperties: PropertyValues<this>): void {
    if (!changedProperties.has('viewModel') || !this.viewModel.open) {
      return;
    }

    this.renderRoot.querySelector<HTMLElement>('.modal')?.focus();
  }

  public render() {
    if (!this.viewModel.open) {
      return nothing;
    }

    return html`
      <div class="backdrop">
        <section
          class="modal"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="session-warning-title"
          aria-describedby="session-warning-message"
          tabindex="-1"
        >
          <p class="eyebrow">Session timeout warning</p>
          <h2 id="session-warning-title">${this.viewModel.title}</h2>
          <p id="session-warning-message">
            ${this.viewModel.message}
            ${this.viewModel.secondsRemaining === undefined
              ? nothing
              : html`<span class="countdown">${this.viewModel.secondsRemaining} seconds remaining.</span>`}
          </p>
          <div class="actions">
            <button class="nhsuk-button" type="button" @click=${this.handleStaySignedIn}>Stay signed in</button>
            <button class="nhsuk-button nhsuk-button--secondary" type="button" @click=${this.handleLogOut}>Log out</button>
          </div>
        </section>
      </div>
    `;
  }

  private readonly handleLogOut = (): void => {
    this.onLogOut?.();
  };

  private readonly handleStaySignedIn = (): void => {
    this.onStaySignedIn?.();
  };
}

customElements.define('inav-session-warning-modal', SessionWarningModal);

declare global {
  interface HTMLElementTagNameMap {
    'inav-session-warning-modal': SessionWarningModal;
  }
}